/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  createContext,
  useMemo,
  useCallback,
  useContext,
  PropsWithChildren,
} from 'react';
import { defaultPortfolioCurrency } from '../utils/consts.ts';
import {
  PortfolioCurrency,
  PortfolioCurrencyDocument,
  usePortfolioCurrencyQuery,
} from '../apollo';

const PortfolioCurrencyProvider = ({ children }: PropsWithChildren) => {
  const { client, data } = usePortfolioCurrencyQuery();

  const currency = useMemo(
    () => data?.portfolioCurrency ?? defaultPortfolioCurrency,
    [data?.portfolioCurrency]
  );

  const setCurrency = useCallback(
    (portfolioCurrency: PortfolioCurrency) => {
      client.writeQuery({
        query: PortfolioCurrencyDocument,
        data: { portfolioCurrency },
      });
    },
    [client]
  );

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
    }),
    [currency, setCurrency]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const Context = createContext<{
  currency: PortfolioCurrency;
  setCurrency: (currency: PortfolioCurrency) => void;
}>({
  currency: defaultPortfolioCurrency,
  setCurrency: () => undefined,
});

export const usePortfolioCurrency = () => useContext(Context);

export { PortfolioCurrencyProvider };
